import { useMemo, useRef, useLayoutEffect } from 'react';

/**
 * Creates a stable reference callback that can model events (user or point in time actions).
 * As a stable reference, the callback will not cause a change making memoized components to
 * re-render.
 *
 * This will become `const useEventCallback = useEvent` or similar when the official react
 * hook launches.
 */
const useEventCallback = <A extends unknown[], R>(
  fn: (...args: A) => R
): ((...args: A) => R) => {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  return useMemo(
    () =>
      (...args) => {
        const { current } = ref;
        return current(...args);
      },
    []
  );
};

export default useEventCallback;
